import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  MapPin, ChevronRight, EyeOff, Navigation, ZoomIn, ZoomOut,
  Maximize2, Minimize2, Footprints, X, Ship, Car, Bot, Package,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogPortal, DialogOverlay } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import type { MapNode, MapConnection, WorldMap, MapVariant } from "@/lib/map";
import {
  DEFAULT_VIEWPORT, ZOOM_LIMITS, CONNECTION_STYLES,
  getNodesForMap,
  isSvgUrl, applyZoomDelta, euclideanDistance,
} from "@/lib/map";
import {
  LocationDetailDialog,
  type LocationDetailEntity,
} from "@/components/play/LocationDetailDialog";
import { MapGeoCanvas } from "@/components/map/MapGeoCanvas";

export interface TrackedAsset {
  name: string;
  location: string;
  icon: string;
  color: string;
}

const ASSET_ICONS: Record<string, React.ComponentType<any>> = {
  ship: Ship, car: Car, drone: Bot, cargo: Package, default: Package,
};

interface WorldMapWidgetProps {
  currentLocation?: string;
  visitedRegions?: string[];
  revealedLocations?: string[];
  worldId?: string;
  worldConfig?: any;
  fogOfWar?: boolean;
  onSendMessage?: (msg: string) => void;
  trackedAssets?: TrackedAsset[];
}

export function WorldMapWidget({
  currentLocation, visitedRegions = [], revealedLocations = [],
  worldId, worldConfig, fogOfWar = false, onSendMessage, trackedAssets = [],
}: WorldMapWidgetProps) {
  const [nodes, setNodes] = useState<MapNode[]>([]);
  const [worldMaps, setWorldMaps] = useState<WorldMap[]>([]);
  const [mapVariants, setMapVariants] = useState<MapVariant[]>([]);
  const [entities, setEntities] = useState<Record<string, { name: string; description: string; image_url: string | null; type: string; properties: Record<string, unknown> }>>({});

  // Map navigation stack — uses WorldMap ids
  const [mapStack, setMapStack] = useState<{ mapId: string | null; label: string }[]>([
    { mapId: null, label: "World Map" },
  ]);

  // Viewport
  const [zoom, setZoom] = useState(DEFAULT_VIEWPORT.zoom);
  const [pan, setPan] = useState(DEFAULT_VIEWPORT.pan);
  const [isPanning, setIsPanning] = useState(false);
  const panStartRef = useRef({ x: 0, y: 0, panX: 0, panY: 0 });
  const viewportRef = useRef<HTMLDivElement>(null);

  // Popup mode
  const [isExpanded, setIsExpanded] = useState(false);

  // Hover travel line
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);

  // Location detail dialog
  const [detailEntity, setDetailEntity] = useState<LocationDetailEntity | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const currentMapId = mapStack[mapStack.length - 1].mapId;
  const currentMap: WorldMap | null = currentMapId ? worldMaps.find(m => m.id === currentMapId) || null : null;

  // Get active variant for current map
  const currentVariant = currentMapId
    ? mapVariants.find(v => v.map_id === currentMapId && v.is_active) || mapVariants.find(v => v.map_id === currentMapId) || null
    : null;

  // Use variant media/config, fallback to map
  const currentMapImage = currentVariant?.media_url || currentMap?.media_url || null;
  const currentRendererConfig = currentVariant?.renderer_config || currentMap?.renderer_config || {};
  const currentRendererType = currentVariant?.renderer_type || currentMap?.renderer_type || "media";
  const isGeoMap = currentRendererType === "geo";
  const geoConfig = currentRendererConfig?.geo_config || null;

  // Get nodes for current map, filtered to active variant (shared + variant-specific)
  const currentNodes = currentMapId ? getNodesForMap(nodes, currentMapId, currentVariant?.id) : [];
  const layers = (currentRendererConfig?.layers || []).map((l: any) => ({ ...l, visible: l.visible ?? true, opacity: l.opacity ?? 1 })).filter((l: any) => l.visible);
  const connections: MapConnection[] = currentRendererConfig?.connections || [];

  const isVisible = (node: MapNode) => {
    if (!fogOfWar) return true;
    if (node.label === currentLocation) return true;
    if (revealedLocations.includes(node.label) || revealedLocations.includes(node.id)) return true;
    if (visitedRegions.includes(node.label)) return true;
    return false;
  };

  useEffect(() => {
    if (!worldId) return;
    const load = async () => {
      // Fetch maps
      const { data: mapsData } = await supabase
        .from("world_maps" as any)
        .select("*")
        .eq("world_id", worldId)
        .order("sort_order");
      const loadedMaps = (mapsData as any as WorldMap[]) || [];
      setWorldMaps(loadedMaps);

      // Fetch variants
      const { data: variantsData } = await supabase
        .from("world_map_variants" as any)
        .select("*")
        .eq("world_id", worldId)
        .order("sort_order");
      setMapVariants((variantsData as any as MapVariant[]) || []);

      // Initialize map stack with root map
      const root = loadedMaps.find(m => !m.parent_map_id);
      if (root) {
        setMapStack([{ mapId: root.id, label: root.name }]);
      }

      // Fetch nodes
      const { data } = await supabase
        .from("world_map_nodes" as any)
        .select("*")
        .eq("world_id", worldId)
        .order("sort_order");
      const mapNodes = (data as any as MapNode[]) || [];
      setNodes(mapNodes);
      const entityIds = mapNodes.filter((n) => n.entity_id).map((n) => n.entity_id!);
      if (entityIds.length > 0) {
        const { data: ents } = await supabase.from("entities").select("id, name, description, image_url, type, properties").in("id", entityIds);
        if (ents) {
          const map: Record<string, any> = {};
          for (const e of ents) map[e.id] = { name: e.name, description: e.description, image_url: e.image_url, type: e.type, properties: e.properties || {} };
          setEntities(map);
        }
      }
    };
    load();
  }, [worldId]);

  // Wheel zoom
  const handleWheel = useCallback((e: WheelEvent) => {
    e.preventDefault();
    setZoom((z) => applyZoomDelta(z, e.deltaY));
  }, []);

  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    vp.addEventListener("wheel", handleWheel, { passive: false });
    return () => vp.removeEventListener("wheel", handleWheel);
  }, [handleWheel]);

  // Pan
  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    const target = e.target as HTMLElement;
    if (target.closest("[data-map-pin]")) return;
    setIsPanning(true);
    panStartRef.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y };
  };
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isPanning) return;
    setPan({
      x: panStartRef.current.panX + (e.clientX - panStartRef.current.x),
      y: panStartRef.current.panY + (e.clientY - panStartRef.current.y),
    });
  };
  const handleMouseUp = () => setIsPanning(false);
  const resetView = () => { setZoom(1); setPan({ x: 0, y: 0 }); };

  // Reset on drill
  useEffect(() => { resetView(); }, [currentMapId]);

  // Auto-pan to current location when it changes
  useEffect(() => {
    if (!currentLocation || nodes.length === 0 || worldMaps.length === 0) return;

    // Find the node matching currentLocation across ALL maps
    const targetNode = nodes.find(n => n.label === currentLocation);
    if (!targetNode || !targetNode.map_id) return;

    // If the target is on a different map, rebuild the map stack
    if (targetNode.map_id !== currentMapId) {
      // Build path from root to target map
      const targetMap = worldMaps.find(m => m.id === targetNode.map_id);
      if (!targetMap) return;

      const buildPath = (mapId: string): { mapId: string; label: string }[] => {
        const map = worldMaps.find(m => m.id === mapId);
        if (!map) return [];
        if (!map.parent_map_id) return [{ mapId: map.id, label: map.name }];
        return [...buildPath(map.parent_map_id), { mapId: map.id, label: map.name }];
      };

      const path = buildPath(targetMap.id);
      if (path.length > 0) {
        setMapStack(path);
      }
    }

    // Center viewport on the node (after a tick for map to update)
    requestAnimationFrame(() => {
      const vp = viewportRef.current;
      if (!vp) return;
      const vpRect = vp.getBoundingClientRect();
      const centerX = -(targetNode.x * vpRect.width * zoom) + vpRect.width / 2;
      const centerY = -(targetNode.y * vpRect.height * zoom) + vpRect.height / 2;
      setPan({ x: centerX, y: centerY });
    });
  }, [currentLocation, nodes.length, worldMaps.length]);

  const drillInto = (node: MapNode) => {
    // Find sub-map for this node
    const linkedMap = node.linked_map_id ? worldMaps.find(m => m.id === node.linked_map_id) : worldMaps.find(m => m.linked_node_id === node.id);
    if (linkedMap) {
      setMapStack(prev => [...prev, { mapId: linkedMap.id, label: linkedMap.name }]);
    }
  };
  const navigateBreadcrumb = (index: number) => setMapStack((prev) => prev.slice(0, index + 1));

  const currentNode = currentLocation ? currentNodes.find((n) => n.label === currentLocation) : null;

  const handlePinClick = (node: MapNode) => {
    const entityInfo = node.entity_id ? entities[node.entity_id] : null;
    if (entityInfo) {
      setDetailEntity({ id: node.entity_id!, ...entityInfo });
      setDetailOpen(true);
    }
  };

  const handleTravel = (locationName: string) => {
    if (onSendMessage && locationName !== currentLocation) {
      onSendMessage(`[TRAVEL_REQUEST] destination: "${locationName}"`);
    }
  };

  const activeMechanicIds = new Set<string>((worldConfig?.mechanics || []).map((m: any) => m.id));
  const showTravelButton = activeMechanicIds.has("world_map");

  if (!currentMapImage && !isGeoMap) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-muted-foreground text-xs">
        <MapPin className="w-8 h-8 mb-2 opacity-30" />
        <span>No map available</span>
      </div>
    );
  }

  // Shared map rendering
  const renderMapContent = (mode: "minimap" | "fullscreen") => {
    // Geo map rendering — use MapGeoCanvas in read-only mode
    if (isGeoMap && geoConfig) {
      return (
        <div
          className="relative rounded-lg overflow-hidden border border-border/30"
          style={{ height: mode === "fullscreen" ? "100%" : 200 }}
        >
          <MapGeoCanvas
            geoConfig={geoConfig}
            currentNodes={currentNodes}
            selectedNode={null}
            connections={connections}
            connectionMode={false}
            connectionFrom={null}
            placingMode={null}
            readOnly
            highlightNodeId={currentNode?.id ?? null}
            onNodeClick={(nodeId) => {
              const node = currentNodes.find(n => n.id === nodeId);
              if (node) handlePinClick(node);
            }}
            displayDefaults={currentRendererConfig?.display_defaults}
            darkMode
          />
        </div>
      );
    }

    // Image-based map rendering
    const maxH = mode === "fullscreen" ? "none" : 200;
    return (
      <div
        ref={mode === "minimap" ? undefined : viewportRef}
        className={cn(
          "relative rounded-lg overflow-hidden border border-border/30",
          isPanning ? "cursor-grabbing" : "cursor-grab",
        )}
        style={{ maxHeight: maxH === "none" ? undefined : maxH, height: mode === "fullscreen" ? "100%" : undefined }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <div
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            transformOrigin: "0 0",
          }}
          className="relative"
        >
          {/* Base map */}
          {isSvgUrl(currentMapImage) ? (
            <object data={currentMapImage} type="image/svg+xml" className="w-full h-auto select-none pointer-events-none" style={{ display: "block" }}>
              <img src={currentMapImage} alt="Map" className="w-full h-auto" loading="lazy" onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
            </object>
          ) : (
            <img src={currentMapImage!} alt="Map" className="w-full h-auto select-none pointer-events-none" draggable={false} loading="lazy" onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
          )}

          {/* Overlay layers */}
          {layers.map((layer) => (
            <div key={layer.id} className="absolute inset-0 pointer-events-none" style={{ opacity: layer.opacity }}>
              {isSvgUrl(layer.imageUrl) ? (
                <object data={layer.imageUrl} type="image/svg+xml" className="w-full h-full" style={{ display: "block" }}>
                  <img src={layer.imageUrl} alt={layer.label} className="w-full h-full object-cover" loading="lazy" onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
                </object>
              ) : (
                <img src={layer.imageUrl} alt={layer.label} className="w-full h-full object-cover" draggable={false} loading="lazy" onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
              )}
            </div>
          ))}

          {/* Connection lines */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 5 }}>
            {connections.map((conn) => {
              const fromNode = currentNodes.find((n) => n.id === conn.from);
              const toNode = currentNodes.find((n) => n.id === conn.to);
              if (!fromNode || !toNode) return null;
              if (fogOfWar && (!isVisible(fromNode) || !isVisible(toNode))) return null;
              const style = CONNECTION_STYLES[conn.style] || CONNECTION_STYLES.road;
              return (
                <line
                  key={conn.id}
                  x1={`${fromNode.x * 100}%`} y1={`${fromNode.y * 100}%`}
                  x2={`${toNode.x * 100}%`} y2={`${toNode.y * 100}%`}
                  stroke={style.color}
                  strokeWidth={style.width}
                  strokeDasharray={style.dash}
                />
              );
            })}
            {/* Travel hover line */}
            {hoveredNode && currentNode && hoveredNode !== currentNode.id && (() => {
              const target = currentNodes.find((n) => n.id === hoveredNode);
              if (!target) return null;
              return (
                <line
                  x1={`${currentNode.x * 100}%`} y1={`${currentNode.y * 100}%`}
                  x2={`${target.x * 100}%`} y2={`${target.y * 100}%`}
                  stroke="hsl(var(--primary) / 0.5)"
                  strokeWidth={2}
                  strokeDasharray="6 4"
                  className="animate-pulse"
                />
              );
            })()}
          </svg>

          {/* Nodes */}
          {currentNodes.map((node) => {
            const visible = isVisible(node);
            const isCurrent = node.label === currentLocation;
            const entityInfo = node.entity_id ? entities[node.entity_id] : null;
            const hasChildren = !!node.linked_map_id || worldMaps.some(m => m.linked_node_id === node.id);

            if (!visible) {
              return (
                <div key={node.id} className="absolute w-3 h-3 rounded-full bg-muted-foreground/20 -translate-x-1/2 -translate-y-1/2" style={{ left: `${node.x * 100}%`, top: `${node.y * 100}%` }}>
                  <EyeOff className="w-2 h-2 text-muted-foreground/30 m-auto mt-0.5" />
                </div>
              );
            }

            if (node.node_type === "area") {
              return (
                <div
                  key={node.id}
                  data-map-pin
                  className={cn("absolute border rounded cursor-pointer transition-colors", isCurrent ? "border-primary bg-primary/15" : "border-foreground/20 bg-foreground/5 hover:bg-foreground/10")}
                  style={{ left: `${node.x * 100}%`, top: `${node.y * 100}%`, width: `${(node.width || 0.1) * 100}%`, height: `${(node.height || 0.1) * 100}%` }}
                  onClick={() => hasChildren ? drillInto(node) : handlePinClick(node)}
                >
                  <span className={cn("absolute -top-3.5 left-1 font-medium bg-background/80 px-1 rounded", mode === "minimap" ? "text-[6px]" : "text-[8px]")}>{node.label}</span>
                </div>
              );
            }

            return (
              <div
                key={node.id}
                data-map-pin
                className="absolute -translate-x-1/2 -translate-y-full cursor-pointer group transition-transform hover:scale-110"
                style={{ left: `${node.x * 100}%`, top: `${node.y * 100}%`, zIndex: isCurrent ? 20 : 10 }}
                onMouseEnter={() => setHoveredNode(node.id)}
                onMouseLeave={() => setHoveredNode(null)}
                onClick={() => handlePinClick(node)}
                onDoubleClick={() => hasChildren && drillInto(node)}
              >
                <div className="flex flex-col items-center">
                  {isCurrent && (
                    <>
                      <div className={cn("absolute rounded-full border-2 border-primary animate-ping opacity-40", mode === "minimap" ? "w-5 h-5 -top-1" : "w-7 h-7 -top-1.5")} />
                      {mode === "fullscreen" && (
                        <span className="absolute -top-5 text-[7px] font-bold text-primary bg-background/90 px-1.5 py-0.5 rounded-full border border-primary/30 whitespace-nowrap z-30">
                          <Navigation className="w-2.5 h-2.5 inline mr-0.5 -mt-px" />You are here
                        </span>
                      )}
                    </>
                  )}
                  <MapPin className={cn("drop-shadow-sm", isCurrent ? "text-primary" : "text-destructive", mode === "minimap" ? "w-3 h-3" : "w-4 h-4")} />
                  <span className={cn("font-medium bg-background/80 px-0.5 rounded whitespace-nowrap truncate", mode === "minimap" ? "text-[6px] max-w-[40px]" : "text-[8px] max-w-[60px]")}>{node.label}</span>
                </div>

                {/* Travel tooltip — only in fullscreen */}
                {mode === "fullscreen" && onSendMessage && showTravelButton && !isCurrent && hoveredNode === node.id && (
                  <div
                    className="absolute -top-8 left-1/2 -translate-x-1/2 bg-background/95 border border-border rounded px-2 py-1 shadow-md whitespace-nowrap z-30"
                    onClick={(e) => { e.stopPropagation(); handleTravel(node.label); }}
                  >
                    <span className="text-[9px] text-primary font-medium flex items-center gap-1">
                      <Footprints className="w-3 h-3" />Travel here
                    </span>
                  </div>
                )}
              </div>
            );
          })}

          {/* Tracked asset markers */}
          {trackedAssets.map((asset) => {
            const assetNode = currentNodes.find(n => n.label === asset.location);
            if (!assetNode) return null;
            const AssetIcon = ASSET_ICONS[asset.icon] || ASSET_ICONS.default;
            return (
              <div
                key={`asset-${asset.name}`}
                className="absolute -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                style={{
                  left: `${assetNode.x * 100 + 1.5}%`,
                  top: `${assetNode.y * 100 - 1.5}%`,
                  zIndex: 15,
                }}
              >
                <div
                  className="flex items-center gap-0.5 rounded-full px-1 py-0.5 border shadow-sm"
                  style={{ backgroundColor: `${asset.color}20`, borderColor: asset.color }}
                >
                  <AssetIcon className={cn("shrink-0", mode === "minimap" ? "w-2 h-2" : "w-3 h-3")} style={{ color: asset.color }} />
                  <span className={cn("font-medium truncate", mode === "minimap" ? "text-[5px] max-w-[30px]" : "text-[7px] max-w-[50px]")} style={{ color: asset.color }}>
                    {asset.name}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Zoom controls overlay — only in fullscreen */}
        {mode === "fullscreen" && (
          <div className="absolute bottom-2 right-2 flex items-center gap-1 bg-background/80 backdrop-blur-sm rounded-md border border-border/50 p-0.5 z-20">
            <span className="text-[9px] text-muted-foreground tabular-nums px-1">{Math.round(zoom * 100)}%</span>
            <Button variant="ghost" size="icon" className="h-6 w-6" aria-label="Zoom in" onClick={() => setZoom((z) => Math.min(ZOOM_LIMITS.max, z + 0.25))}>
              <ZoomIn className="w-3 h-3" />
            </Button>
            <Button variant="ghost" size="icon" className="h-6 w-6" aria-label="Zoom out" onClick={() => setZoom((z) => Math.max(ZOOM_LIMITS.min, z - 0.25))}>
              <ZoomOut className="w-3 h-3" />
            </Button>
            <Button variant="ghost" size="icon" className="h-6 w-6" aria-label="Reset view" onClick={resetView}>
              <Maximize2 className="w-3 h-3" />
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-2">
      {/* Breadcrumb */}
      {mapStack.length > 1 && (
        <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
          {mapStack.map((crumb, i) => (
            <span key={i} className="flex items-center gap-0.5">
              {i > 0 && <ChevronRight className="w-2.5 h-2.5" />}
              <button onClick={() => navigateBreadcrumb(i)} className={cn("hover:text-foreground transition-colors", i === mapStack.length - 1 ? "text-foreground font-medium" : "")}>
                {crumb.label}
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Minimap view (inline, compact) */}
      <div className="relative group">
        {renderMapContent("minimap")}
        {/* Expand button overlay */}
        <Button
          variant="secondary"
          size="icon"
          className="absolute top-1.5 right-1.5 h-6 w-6 opacity-60 group-hover:opacity-100 transition-opacity z-20 shadow-sm"
          onClick={() => setIsExpanded(true)}
          title="Open full map"
          aria-label="Open full map"
        >
          <Maximize2 className="w-3 h-3" />
        </Button>
      </div>

      {/* Current location indicator */}
      {currentLocation && (
        <div className="flex items-center gap-1.5 text-xs text-primary">
          <Navigation className="w-3 h-3" />
          <span className="font-medium">{currentLocation}</span>
        </div>
      )}

      {/* Fullscreen map popup — custom content to avoid double X */}
      <Dialog open={isExpanded} onOpenChange={setIsExpanded}>
        <DialogPortal>
          <DialogOverlay />
          <div className="fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%] max-w-[95vw] w-[95vw] max-h-[90vh] h-[90vh] border bg-background shadow-lg sm:rounded-lg overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-4 py-2 border-b border-border shrink-0">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-primary" />
                <h2 className="text-sm font-semibold text-foreground">World Map</h2>
                {currentLocation && (
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Navigation className="w-3 h-3 text-primary" /> {currentLocation}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {mapStack.length > 1 && (
                  <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                    {mapStack.map((crumb, i) => (
                      <span key={i} className="flex items-center gap-0.5">
                        {i > 0 && <ChevronRight className="w-2.5 h-2.5" />}
                        <button onClick={() => navigateBreadcrumb(i)} className={cn("hover:text-foreground transition-colors", i === mapStack.length - 1 ? "text-foreground font-medium" : "")}>
                          {crumb.label}
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <Button variant="ghost" size="sm" className="h-7 gap-1.5 text-xs" onClick={() => setIsExpanded(false)}>
                  <X className="w-3.5 h-3.5" />
                  Close
                </Button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden p-2" ref={viewportRef}>
              {renderMapContent("fullscreen")}
            </div>
          </div>
        </DialogPortal>
      </Dialog>

      {/* Location detail dialog */}
      <LocationDetailDialog
        entity={detailEntity}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        onTravel={handleTravel}
        currentLocation={currentLocation}
        showTravelButton={showTravelButton}
      />
    </div>
  );
}

export function LocationStatusWidget({ currentLocation }: { currentLocation?: string }) {
  if (!currentLocation) return null;
  return (
    <div className="flex items-center gap-1 text-[10px]">
      <MapPin className="w-3 h-3 text-primary" />
      <span className="truncate max-w-[80px]">{currentLocation}</span>
    </div>
  );
}
